/* @flow */


import { getRandom } from '../utils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { BrowserUtil, isJSONSupported, isSecure } from '../utils/browserutil';
import { isDNT } from '../utils/privacyutils';
import { logError } from '../utils/logger';
import { extend, param } from '../utils/coreutils';
import adconfig from '../adconfig';
import { getRTBUserEids } from '../idproviders/idputils';

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * typedef definitions
 */
declare type pubSlotData = {
  id: string,
  tagid: string,
  secure: 0 | 1,
  bidfloorcur: 'USD',
  banner: {
    w: number,
    h: number,
    topframe: number,
    format: Array<{ w: number, h: number }>
  }
};
declare type pubReqParam = {
  id: string,
  imp: Array<pubSlotData>,
  at: 1,
  device: {
    ua: string,
    dnt: 0 | 1,
    h: string,
    w: string,
    language: string
  },
  site: {
    publisher: {
      id: string
    },
    page: string,
    ref: string
  }
};

declare type pubBidRequestCache = {
  impid: {
    [string]: string
  }
};

/**
 * @module bidders/pubmatic
 * @requires module:logger
 * @requires module:coreutils
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=puhb]
 * @property {string} id
 */
/**
 * @description Implements functionality for pubmatic header bidding
 * @exports pubmaticbid
 * @augments _bidbase
 * @requires module:utils
 * @requires module:adloader
 */

class _pubmatic extends bidbase {
  _bidRequest: pubBidRequestCache;

  _config: pubmaticConfig;

  _DefConfig: pubmaticConfig;

  constructor(name: string = 'pubmatic') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      pubID: '',
      qpKey: 'puhb'
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl', 'pubID'];
    this._bidRequest = {
      impid: {}
    };
  }

  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object} response - Response from server
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    response: RTBBidResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!this._isValidRTBResponse(response)) {
      return ret;
    }
    response.seatbid[0].bid.forEach(bid => {
      if (bid && bid.impid && bid.price && bid.adm) {
        const { impid, price, adm } = bid;
        this._handleBidPosData(
          ret,
          this._bidRequest.impid[impid],
          price,
          impid,
          adm
        );
      }
    });
    return ret;
  }

  /**
   * Create slot object for an ad position
   *
   * @param {string} pos - Ad position
   * @returns {object} - Request URL info for given position
   * @access private
   */
  _getSlot(pos: string): pubSlotData {
    // get all the required params
    const randid = getRandom();
    const sizes = [...this._config.posTokenMap[pos].sizes];
    let width = 0;
    let height = 0;
    if (sizes.length) {
      [width, height] = sizes.shift();
    }
    const fmt = sizes.map(([wt, ht]) => ({ w: wt, h: ht }));
    const gpid = adconfig.getGPID(pos);
    const params = {
      id: randid,
      tagid: this._config.posTokenMap[pos].adTagId,
      secure: isSecure() ? 1 : 0,
      bidfloorcur: 'USD',
      ext: {
        // eslint-disable-next-line camelcase
        dfp_ad_unit_code: gpid
      },
      banner: {
        w: width,
        h: height,
        topframe: this._config.posTokenMap[pos].iframe === false ? 1 : 0,
        format: fmt
      }
    };
    this._bidRequest.impid[randid] = pos;
    return params;
  }

  /**
   * Build request params in RTB format
   *
   * @param {Array<object>} slots - Ad position
   * @returns {object} - Request URL info for given positions
   * @access private
   */
  _buildReqParams(slots: Array<pubSlotData>): pubReqParam {
    // build params for bid request
    const params = {
      id: getRandom(),
      site: {
        publisher: {
          id: this._config.pubID
        },
        page: window.location.href || '',
        ref: document.referrer || ''
      },
      device: {
        ua: BrowserUtil.getUserAgent(),
        dnt: isDNT() ? 1 : 0,
        h: window.screen.height || '',
        w: window.screen.width || '',

        language: navigator.language || ''
      },
      imp: slots,
      at: 1
    };
    return params;
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Promise<RTBBidResponse> {
    const slots = posList.map(pos => this._getSlot(pos));
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${posList.join(',')}`
    };

    const eids = getRTBUserEids();

    const params = extend(
      true,
      {},
      this._buildReqParams(slots),
      {
        // similar to prebid (openRTB)
        regs: { ext: this._getPrivacyString() }
      },
      eids
    );
    const req = {
      method: 'POST',
      body: JSON.stringify(params),
      credentials: 'include'
    };
    return fetchJSON(this._config.bidUrl, opt, req).then(
      (json: RTBBidResponse) => json
    );
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const ret = [];
    // make sure that syncUrl & pubID is not empty
    if (this._config.syncUrl.length > 0 && this._config.pubID.length > 0) {
      ret.push({
        name: this.getName(),
        type: 'iframe',
        url: `${this._config.syncUrl}${this._config.pubID}&${param(
          this._getPrivacyString()
        )}`
      });
    }
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    return this._getBids(posList).then(
      response => this._processBidResponse(posList, response, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

const pubmatic: _pubmatic = new _pubmatic();
export default pubmatic;
